import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Building2, Search, MapPin, Phone, Globe, Clock,
  Plus, Utensils, Bed, HeartPulse, Briefcase, Car, ShoppingBag,
  GraduationCap, Music, Sparkles, Wrench, Home, Grid, Mail, ExternalLink, X,
  ChevronLeft, ChevronRight, ImageIcon, Share2
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

const categoryIcons = {
  "food": Utensils,
  "tourism": Bed,
  "health": HeartPulse,
  "services": Briefcase,
  "transport": Car,
  "shops": ShoppingBag,
  "education": GraduationCap,
  "entertainment": Music,
  "beauty": Sparkles,
  "automotive": Wrench,
  "real-estate": Home,
  "other": Grid,
};

function BusinessCard({ business, categories, onSelect }) {
  const category = categories.find(c => c.id === business.category);
  const IconComponent = categoryIcons[business.category] || Grid;
  const image = getImageUrl(business.featured_image_url);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group"
    >
      <div
        onClick={() => onSelect(business)}
        className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 cursor-pointer"
        data-testid={`business-card-${business.slug}`}
      >
        {/* Image or placeholder */}
        <div className="relative aspect-[16/9] bg-gray-100 overflow-hidden">
          {image ? (
            <img
              src={image}
              alt={business.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
              <IconComponent className="h-12 w-12 text-primary/40" />
            </div>
          )}
          <Badge className="absolute top-3 left-3 bg-white text-brand-text shadow-sm">
            <IconComponent className="h-3 w-3 mr-1" />
            {category?.name || "Otros"}
          </Badge>
        </div>

        <div className="p-5">
          <h3 className="font-heading text-lg font-bold text-brand-text mb-2 line-clamp-1">
            {business.name}
          </h3>
          
          {business.description && (
            <p className="text-brand-textSecondary text-sm line-clamp-2 mb-3">
              {business.description}
            </p>
          )}
          
          <div className="space-y-1.5 text-sm text-brand-textMuted">
            {business.address && (
              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="truncate">{business.address}</span>
              </p>
            )}
            {business.phone && (
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span>{business.phone}</span>
              </p>
            )}
            {business.hours && (
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="truncate">{business.hours}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function DirectorioPage() {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    services: "",
    phone: "",
    whatsapp: "",
    email: "",
    address: "",
    municipio: "",
    website: "",
    facebook: "",
    instagram: "",
    hours: "",
    submitter_name: "",
    submitter_email: "",
  });

  useEffect(() => {
    fetchCategories();
    // Track page view
    axios.post(`${API}/track-page-view?page=directorio`).catch(() => {});
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/business/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      let url = `${API}/public/businesses?limit=50`;
      if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url);
      setBusinesses(response.data.items);
    } catch (error) {
      console.error("Error fetching businesses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category || !formData.submitter_name || !formData.submitter_email) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        services: formData.services ? formData.services.split(',').map(s => s.trim()) : [],
      };
      
      await axios.post(`${API}/business/submit`, payload);
      toast.success("¡Gracias! Tu empresa será revisada pronto.");
      setSubmitDialogOpen(false);
      setFormData({
        name: "", category: "", description: "", services: "",
        phone: "", whatsapp: "", email: "", address: "", municipio: "",
        website: "", facebook: "", instagram: "", hours: "",
        submitter_name: "", submitter_email: "",
      });
    } catch (error) {
      toast.error("Error al enviar. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <PageHero
        section="directorio"
        title="Directorio de Empresas"
        subtitle="Encuentra los mejores negocios y servicios de Choluteca"
        badge="Negocios Locales"
        badgeIcon={Building2}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <Button
            size="lg"
            onClick={() => setSubmitDialogOpen(true)}
            className="bg-accent text-brand-text hover:bg-accent/90 rounded-full"
            data-testid="add-business-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Agregar mi empresa
          </Button>
        </motion.div>
      </PageHero>

      {/* Filters */}
      <section className="py-6 bg-white border-b sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Buscar negocios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="business-search"
              />
            </div>
            
            {/* Category filters */}
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => setSelectedCategory("")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  !selectedCategory
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Todos
              </button>
              {categories.slice(0, 6).map((cat) => {
                const IconComponent = categoryIcons[cat.id] || Grid;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                      selectedCategory === cat.id
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                    {cat.name.split(' ')[0]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Businesses Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-[16/9] bg-gray-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : businesses.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business, index) => (
                <motion.div
                  key={business.business_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <BusinessCard business={business} categories={categories} onSelect={setSelectedBusiness} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No hay empresas</h3>
              <p className="text-gray-500 mb-6">
                {searchQuery || selectedCategory
                  ? "No se encontraron negocios con esos filtros"
                  : "Sé el primero en registrar tu empresa"}
              </p>
              <Button onClick={() => setSubmitDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Agregar mi empresa
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Submit Dialog */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar mi empresa</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre de la empresa *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del negocio"
                  required
                />
              </div>
              
              <div className="col-span-2">
                <Label>Categoría *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe tu negocio"
                  rows={3}
                />
              </div>

              <div className="col-span-2">
                <Label>Servicios (separados por coma)</Label>
                <Input
                  value={formData.services}
                  onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                  placeholder="Ej: Comida rápida, Delivery, Para llevar"
                />
              </div>

              <div>
                <Label>Teléfono</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+504 0000-0000"
                />
              </div>

              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  placeholder="+504 0000-0000"
                />
              </div>

              <div>
                <Label>Email del negocio</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@empresa.com"
                />
              </div>

              <div>
                <Label>Sitio web</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="col-span-2">
                <Label>Dirección</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Dirección completa"
                />
              </div>

              <div>
                <Label>Municipio</Label>
                <Input
                  value={formData.municipio}
                  onChange={(e) => setFormData({ ...formData, municipio: e.target.value })}
                  placeholder="Ej: Choluteca"
                />
              </div>

              <div>
                <Label>Horario de atención</Label>
                <Input
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                  placeholder="Ej: Lun-Vie 9am-6pm"
                />
              </div>

              <div>
                <Label>Facebook</Label>
                <Input
                  value={formData.facebook}
                  onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                  placeholder="https://facebook.com/..."
                />
              </div>

              <div>
                <Label>Instagram</Label>
                <Input
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  placeholder="https://instagram.com/..."
                />
              </div>

              <div className="col-span-2 border-t pt-4 mt-2">
                <p className="text-sm text-gray-500 mb-4">Tus datos de contacto (no se mostrarán públicamente)</p>
              </div>

              <div>
                <Label>Tu nombre *</Label>
                <Input
                  value={formData.submitter_name}
                  onChange={(e) => setFormData({ ...formData, submitter_name: e.target.value })}
                  placeholder="Tu nombre completo"
                  required
                />
              </div>

              <div>
                <Label>Tu email *</Label>
                <Input
                  type="email"
                  value={formData.submitter_email}
                  onChange={(e) => setFormData({ ...formData, submitter_email: e.target.value })}
                  placeholder="tu@email.com"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setSubmitDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Enviando..." : "Enviar para revisión"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Business Detail Dialog */}
      <Dialog open={!!selectedBusiness} onOpenChange={() => setSelectedBusiness(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedBusiness && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selectedBusiness.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Image Gallery Carousel */}
                {(selectedBusiness.featured_image_url || (selectedBusiness.gallery_urls && selectedBusiness.gallery_urls.length > 0)) && (
                  <div className="relative">
                    {/* Build all images array: featured + gallery */}
                    {(() => {
                      const allImages = [];
                      if (selectedBusiness.featured_image_url) {
                        allImages.push({ url: selectedBusiness.featured_image_url, isFeatured: true });
                      }
                      if (selectedBusiness.gallery_urls && selectedBusiness.gallery_urls.length > 0) {
                        selectedBusiness.gallery_urls.forEach(img => {
                          allImages.push({ url: typeof img === 'string' ? img : img.url, isFeatured: false });
                        });
                      }
                      
                      if (allImages.length === 1) {
                        // Single image - no carousel needed
                        return (
                          <div className="aspect-video rounded-xl overflow-hidden bg-gray-100">
                            <img
                              src={getImageUrl(allImages[0].url)}
                              alt={selectedBusiness.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        );
                      }
                      
                      // Multiple images - use carousel
                      return (
                        <Carousel className="w-full" opts={{ loop: true }}>
                          <CarouselContent>
                            {allImages.map((img, index) => (
                              <CarouselItem key={index}>
                                <div className="aspect-video rounded-xl overflow-hidden bg-gray-100">
                                  <img
                                    src={getImageUrl(img.url)}
                                    alt={`${selectedBusiness.name} - ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              </CarouselItem>
                            ))}
                          </CarouselContent>
                          <CarouselPrevious className="left-2 bg-white/80 hover:bg-white" />
                          <CarouselNext className="right-2 bg-white/80 hover:bg-white" />
                          {/* Image counter */}
                          <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <ImageIcon className="h-3 w-3" />
                            {allImages.length} fotos
                          </div>
                        </Carousel>
                      );
                    })()}
                  </div>
                )}

                {/* Category Badge */}
                <div>
                  <Badge className="bg-primary/10 text-primary">
                    {categories.find(c => c.id === selectedBusiness.category)?.name || "Sin categoría"}
                  </Badge>
                </div>

                {/* Description */}
                {selectedBusiness.description && (
                  <p className="text-brand-textSecondary">{selectedBusiness.description}</p>
                )}

                {/* Services */}
                {selectedBusiness.services && selectedBusiness.services.length > 0 && (
                  <div>
                    <h4 className="font-medium text-brand-text mb-2">Servicios</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedBusiness.services.map((service, i) => (
                        <Badge key={i} variant="outline">{service}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h4 className="font-medium text-brand-text">Información de Contacto</h4>
                  
                  {selectedBusiness.address && (
                    <p className="flex items-center gap-3 text-sm">
                      <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>{selectedBusiness.address}</span>
                    </p>
                  )}
                  
                  {selectedBusiness.phone && (
                    <a href={`tel:${selectedBusiness.phone}`} className="flex items-center gap-3 text-sm text-primary hover:underline">
                      <Phone className="h-5 w-5 flex-shrink-0" />
                      <span>{selectedBusiness.phone}</span>
                    </a>
                  )}
                  
                  {selectedBusiness.whatsapp && (
                    <a href={`https://wa.me/${selectedBusiness.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-green-600 hover:underline">
                      <Phone className="h-5 w-5 flex-shrink-0" />
                      <span>WhatsApp: {selectedBusiness.whatsapp}</span>
                    </a>
                  )}
                  
                  {selectedBusiness.email && (
                    <a href={`mailto:${selectedBusiness.email}`} className="flex items-center gap-3 text-sm text-primary hover:underline">
                      <Mail className="h-5 w-5 flex-shrink-0" />
                      <span>{selectedBusiness.email}</span>
                    </a>
                  )}
                  
                  {selectedBusiness.hours && (
                    <p className="flex items-center gap-3 text-sm">
                      <Clock className="h-5 w-5 text-gray-500 flex-shrink-0" />
                      <span>{selectedBusiness.hours}</span>
                    </p>
                  )}
                  
                  {selectedBusiness.website && (
                    <a href={selectedBusiness.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-primary hover:underline">
                      <Globe className="h-5 w-5 flex-shrink-0" />
                      <span>Sitio web</span>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>

                {/* Social Links */}
                {(selectedBusiness.facebook || selectedBusiness.instagram) && (
                  <div className="flex gap-3">
                    {selectedBusiness.facebook && (
                      <a href={selectedBusiness.facebook} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm hover:bg-blue-700 transition-colors">
                        Facebook
                      </a>
                    )}
                    {selectedBusiness.instagram && (
                      <a href={selectedBusiness.instagram} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full text-sm hover:opacity-90 transition-opacity">
                        Instagram
                      </a>
                    )}
                  </div>
                )}

                {/* Share / View Full Page Button */}
                <div className="pt-4 border-t">
                  <Link 
                    to={`/directorio/${selectedBusiness.slug || selectedBusiness.business_id}`}
                    className="block"
                  >
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="view-full-page-btn">
                      <Share2 className="h-4 w-4 mr-2" />
                      Ver página completa y compartir
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
